// /board: the wall screen. Chrome-free, a 1920×1080 frame scaled to the
// screen (BoardFrame), slides in the order and timing Settings gives. The week
// slide is the main one: a "you are here" line, blocks turning green as
// sessions are logged (with a celebration), a warning where an issue was
// reported. ?slide=<id> holds one slide (for screenshots); ?demo=celebrate in
// fixture mode logs a session after a few seconds.
import { IonContent, IonPage } from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { BoardView, Cell, SessionRow, SkillRow, TaskRow } from "../lib/types";
import { BlockCell, BoardFrame, BuildFlow, Celebration, NowLine, PreviewBanner, ShelfGauge, SlotHeader, StatusBanner } from "../design";
import { announce, useApi, useMe } from "../api/live";
import { fixtureMode } from "../api/mode";
import { paths } from "../api/paths";
import { blockInfo, sessionFor } from "../lib/blockState";
import { clock, dayMonth, HALVES, shortDay, weekday, weekdayLong } from "../lib/days";
import { useNow } from "../lib/now";
import { useTasks } from "../lib/useRegister";
import { forecastSentence } from "./WeekPage";
import { list, safeBoard } from "../lib/safe";

const DEFAULT_SLIDES = [{ id: "week", seconds: 40 }, { id: "shelves", seconds: 20 }, { id: "yesterday", seconds: 15 }, { id: "stats", seconds: 15 }, { id: "skills", seconds: 15 }, { id: "flow", seconds: 15 }];
const TITLES: Record<string, string> = { week: "This week", shelves: "Shelves", yesterday: "Yesterday", stats: "Devices by day", skills: "Skills", flow: "Build flow" };

export function BoardPage() {
  const { me } = useMe();
  const board = useApi<BoardView>(paths.board(), "all");
  const skills = useApi<SkillRow[]>(paths.skills(), ["skill"]);
  const { byCode, tasks } = useTasks();
  const now = useNow();
  const pinned = new URLSearchParams(window.location.search).get("slide");
  const [i, setI] = useState(0);
  const [loadedAt, setLoadedAt] = useState(() => Date.now());
  const [party, setParty] = useState<{ key: string; text: string; detail: string } | null>(null);
  const seen = useRef<Set<number> | null>(null);

  const b = useMemo(() => (board.data ? safeBoard(board.data) : null), [board.data]);
  const slides = (b?.slides?.length ? b.slides : DEFAULT_SLIDES).filter((s) => TITLES[s.id]);
  const slide = pinned && TITLES[pinned] ? pinned : slides[i % slides.length]?.id ?? "week";

  // Slides move on by themselves; a celebration holds the week slide.
  useEffect(() => {
    if (pinned) return;
    const secs = slides[i % slides.length]?.seconds ?? 20;
    const t = setTimeout(() => setI((x) => x + 1), secs * 1000);
    return () => clearTimeout(t);
  }, [i, pinned, slides]);

  // A newly logged session that met its target: green, confetti, and the week slide.
  useEffect(() => {
    if (!b) return;
    setLoadedAt(Date.now());
    const ids = new Set(b.week.sessions.map((s) => s.id));
    if (seen.current) {
      const fresh = b.week.sessions.filter((s) => !seen.current!.has(s.id) && s.status === "logged");
      const win = fresh.find((s) => {
        const cell = b.week.cells.find((c) => c.person === s.person && c.day === s.day && c.half === s.half);
        return cell && ["done", "short"].includes(blockInfo(cell, { session: s, sessionQty: byCode[cell.task ?? ""]?.sessionQty }).state);
      });
      if (win) {
        setParty({ key: `${win.person}|${win.day}|${win.half}`, text: `${win.person} made ${win.actualQty}${win.task ? ` ${win.task}` : ""}`, detail: win.plannedQty ? `of ${win.plannedQty} planned` : "logged" });
        if (!pinned) setI(slides.findIndex((s) => s.id === "week"));
      }
    }
    seen.current = ids;
  }, [b]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fixtureMode() || new URLSearchParams(window.location.search).get("demo") !== "celebrate") return;
    const t = setTimeout(async () => { const { fixtureLogDemo } = await import("../fixtures/server"); if (fixtureLogDemo()) announce(["session"]); }, 3000);
    return () => clearTimeout(t);
  }, []);

  const stale = Date.now() - loadedAt > 5 * 60_000;

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <BoardFrame>
          <PreviewBanner preview={me?.preview} night />
          <div className="bd">
            <header className="bd__top">
              <span className="bd__brand">OnlyCat</span>
              <span className="bd__day">{weekdayLong(now.day)} {dayMonth(now.day)} · {TITLES[slide]}</span>
              <span className="bd__clock">{clock(now.minute)}</span>
            </header>
            <main className="bd__main">
              {!b ? <p className="bd-hero__k">{board.error ? `The board can't reach the planner: ${board.error.message}` : "Reading today's week…"}</p>
                : slide === "week" ? <WeekSlide b={b} byCode={byCode} now={now} party={party?.key ?? null} />
                : slide === "shelves" ? <ShelvesSlide b={b} />
                : slide === "yesterday" ? <YesterdaySlide b={b} byCode={byCode} />
                : slide === "stats" ? <StatsSlide b={b} today={now.day} />
                : slide === "skills" ? <SkillsSlide skills={list(skills.data)} tasks={tasks} />
                : <FlowSlide b={b} tasks={tasks} />}
            </main>
            <footer className="bd__foot">
              <div className="bd__dots" aria-hidden="true">{slides.map((s) => <span key={s.id} className={`bd__dot${s.id === slide ? " is-on" : ""}`} />)}</div>
              {b ? <span>{forecastSentence(b.week.forecast, b.week.days)}</span> : null}
              <span className={`bd__updated${stale ? " is-stale" : ""}`}>{stale ? "Not updated since " : "Updated "}{new Date(loadedAt).toTimeString().slice(0, 5)}</span>
            </footer>
          </div>
          <Celebration show={!!party} contained night message={party?.text} detail={party?.detail} onDone={() => setParty(null)} />
        </BoardFrame>
      </IonContent>
    </IonPage>
  );
}

function WeekSlide({ b, byCode, now, party }: { b: BoardView; byCode: Record<string, TaskRow>; now: { day: string; half: "AM" | "PM"; minute: number }; party: string | null }) {
  const w = b.week;
  const approved = w.versions.find((v) => v.kind === "approved") ?? null;
  const today = w.days.includes(now.day) ? now.day : null;
  const finishedToday = w.sessions.filter((s) => s.day === now.day && s.task === "A3").reduce((n, s) => n + (s.actualQty ?? 0), 0);
  const forecastToday = today ? w.forecast.days[today] ?? 0 : 0;
  const rows = w.people.filter((p) => w.cells.some((c) => c.person === p.name && c.kind !== "off"));
  return (
    <div className="bd-week">
      <section className="bd-card bd-hero">
        <span className="bd-hero__k">Finished today</span>
        <span className="bd-hero__n">{finishedToday}</span>
        <span className="bd-hero__of">devices · forecast {forecastToday}</span>
        <div className="bd-hero__week">
          <span>This week</span>
          <strong>{w.forecast.week} devices forecast</strong>
          <span>{w.days.map((d) => `${weekday(d)} ${w.forecast.days[d] ?? 0}`).join(" · ")}</span>
        </div>
        <div style={{ marginTop: "var(--space-5)" }}><StatusBanner shown={w.shown ?? approved} night /></div>
      </section>
      <section className="bd-card">
        <div className="bd-grid" style={{ gridTemplateRows: `auto auto repeat(${rows.length}, minmax(0, 1fr))`, height: "100%" }}>
          <div style={{ gridRow: "1 / span 2", gridColumn: 1 }} />
          <SlotHeader days={w.days} today={today} night />
          {rows.map((p, r) => (
            <BoardRow key={p.name} row={r + 3} name={p.name} cells={w.cells} sessions={w.sessions} days={w.days} byCode={byCode} now={now} lockedBefore={w.lockedBefore} party={party} />
          ))}
          <div className="bd-grid__now" style={{ gridColumn: "2 / -1", gridRow: `3 / ${rows.length + 3}` }}>
            <NowLine days={w.days} now={today ? now : null} night />
          </div>
        </div>
      </section>
    </div>
  );
}

function BoardRow({ row, name, cells, sessions, days, byCode, now, lockedBefore, party }: {
  row: number; name: string; cells: Cell[]; sessions: SessionRow[]; days: string[]; byCode: Record<string, TaskRow>;
  now: { day: string; half: "AM" | "PM" }; lockedBefore: BoardView["week"]["lockedBefore"]; party: string | null;
}) {
  return (
    <>
      <div className="bd-grid__name" style={{ gridRow: row, gridColumn: 1 }}>{name}</div>
      {days.flatMap((d, i) => HALVES.map((h, j) => {
        const c = cells.find((x) => x.person === name && x.day === d && x.half === h) ?? { person: name, day: d, half: h, kind: "off" as const };
        const t = c.task ? byCode[c.task] : undefined;
        return (
          <div key={d + h} style={{ gridRow: row, gridColumn: 2 + i * 2 + j, minWidth: 0 }}>
            <BlockCell cell={c} night taskName={null} group={t?.group} sessionQty={t?.sessionQty} halfOnly={t?.halfOnly}
              session={sessionFor(sessions, c)} now={now} lockedBefore={lockedBefore} celebrating={party === `${c.person}|${c.day}|${c.half}`} />
          </div>
        );
      }))}
    </>
  );
}

function ShelvesSlide({ b }: { b: BoardView }) {
  // Reddest first: what the floor most needs to see from across the room.
  const rank = { red: 0, amber: 1, unknown: 2, green: 3 } as const;
  const shelves = b.shelves.filter((s) => !s.isOutput && s.countMode === "number").sort((x, y) => rank[x.state] - rank[y.state]).slice(0, 16);
  return (
    <section className="bd-card" style={{ flex: 1 }}>
      <h2 className="bd-pane-title">At the last 16:45 count</h2>
      <div className="bd-shelves">{shelves.map((s) => <ShelfGauge key={s.code} shelf={s} variant="board" />)}</div>
    </section>
  );
}

function YesterdaySlide({ b, byCode }: { b: BoardView; byCode: Record<string, TaskRow> }) {
  const y = b.yesterday;
  if (!y) return <section className="bd-card" style={{ flex: 1 }}><h2 className="bd-pane-title">Nothing logged yesterday</h2></section>;
  const made = y.sessions.reduce((n, s) => n + (s.actualQty ?? 0), 0);
  return (
    <section className="bd-card" style={{ flex: 1 }}>
      <h2 className="bd-pane-title">{shortDay(y.day)}: {y.devices} devices finished, {made} units made in {y.sessions.length} sessions</h2>
      <div className="bd-list">
        {y.sessions.slice(0, 16).map((s) => (
          <div key={s.id} className="bd-list__row">
            <span className="bd-list__who">{s.person} {s.half}</span>
            <span>{s.task ? `${s.task} ${byCode[s.task]?.name ?? ""}` : "Other work"}</span>
            <span className="bd-list__n">{s.actualQty ?? 0}{s.plannedQty ? ` / ${s.plannedQty}` : ""}</span>
          </div>
        ))}
      </div>
      {y.sessions.some((s) => s.issue) ? (
        <p className="bd-list__note" style={{ marginTop: "var(--space-5)" }}>Issues: {y.sessions.filter((s) => s.issue).map((s) => `${s.person}: ${s.issue}`).join(" · ")}</p>
      ) : null}
    </section>
  );
}

function StatsSlide({ b, today }: { b: BoardView; today: string }) {
  const days = b.stats.devicesByDay.slice(-15);
  const top = Math.max(10, ...days.map((d) => d.devices));
  return (
    <section className="bd-card" style={{ flex: 1 }}>
      <h2 className="bd-pane-title">Devices finished, last three weeks</h2>
      <div className="bd-bars">
        {days.map((d) => (
          <div key={d.day} className="bd-bar">
            <span className="bd-bar__n">{d.devices}</span>
            <span className={`bd-bar__fill${d.day === today ? " is-today" : ""}`} style={{ height: `${(d.devices / top) * 100}%` }} />
            <span>{weekday(d.day)} {dayMonth(d.day)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function SkillsSlide({ skills, tasks }: { skills: SkillRow[]; tasks: TaskRow[] }) {
  const people = useMemo(() => [...new Set(skills.map((s) => s.person))], [skills]);
  const codes = tasks.filter((t) => !t.code.includes("/")).map((t) => t.code).filter((c) => skills.some((s) => s.task === c)).slice(0, 14);
  const at = (p: string, t: string) => skills.find((s) => s.person === p && s.task === t);
  return (
    <section className="bd-card" style={{ flex: 1 }}>
      <h2 className="bd-pane-title">Who can do what</h2>
      <table className="bd-skills">
        <thead><tr><th />{codes.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>
          {people.map((p) => (
            <tr key={p}><td>{p}</td>{codes.map((c) => { const s = at(p, c); const lv = s?.level ?? 0;
              return <td key={c} className={`lv--${lv}`}>{lv ? (s?.expert ? "★" : lv) : ""}</td>; })}</tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function FlowSlide({ b, tasks }: { b: BoardView; tasks: TaskRow[] }) {
  const today = b.now?.day;
  const who: Record<string, string[]> = {};
  for (const c of b.week.cells) if (c.day === today && c.kind === "task" && c.task) (who[c.task] ??= []).includes(c.person) || who[c.task].push(c.person);
  return (
    <section className="bd-card" style={{ flex: 1 }}>
      <BuildFlow tasks={tasks} shelves={b.shelves} night compact peopleByTask={who} />
    </section>
  );
}
